import React, { Suspense, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { renderMarkdown } from '../utils/markdown';
import {
  Tree, Button, Space, Typography, Empty, Spin, message,
  Modal, Input, Tooltip, Tag, Breadcrumb, Segmented, Popconfirm,
} from 'antd';
import type { TreeDataNode } from 'antd';
import {
  FolderOutlined, FileMarkdownOutlined, SaveOutlined,
  FolderAddOutlined, FileAddOutlined,
  EditOutlined, EyeOutlined, DeleteOutlined, CodeOutlined,
  ReloadOutlined, ExclamationCircleOutlined, ApartmentOutlined, CloseOutlined, FullscreenOutlined,
  SearchOutlined, ArrowLeftOutlined, GlobalOutlined,
} from '@ant-design/icons';
import ReactFlow, {
  Background, Controls, type Node as RFNode, type Edge as RFEdge,
  BackgroundVariant, Handle, Position, type NodeProps, MarkerType,
  useNodesState, useEdgesState, useStore, BaseEdge, getStraightPath, type EdgeProps,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { useUser } from '../contexts/UserContext';
import { BrainFile } from '../types';
import { EXECUTOR_API } from '../types';
import { buildAuthHeaders } from '../utils/userStore';
import { colors, glass, glassBarStyle, glassDivider, glassInsetStyle } from '../theme';
import { GlassWorkspace, GlassPanel } from './common/GlassWorkspace';
import {
  fetchBrainTree,
  fetchBrainFile,
  saveBrainFile,
  deleteBrainFile,
  deleteBrainPath,
} from '../services/api.service';
import { useBrainIndexing } from '../contexts/BrainIndexingContext';
import { useActiveOrganization } from '../hooks/useActiveOrganization';
import type { IndexingJobSpec } from '../utils/brainIndexingJob';
import { LABEL_REACH, NODE_WIDTH, RING_NODE_SIZE, graphSignature, layoutGraph } from '../utils/graphLayout';
import { normalizeGraph } from '../utils/graphNormalize';
import { startedMessage } from '../utils/brainIndexingMessages';

const { Text } = Typography;
const { TextArea } = Input;

// The visual editor (and its Markdown engine) is only fetched when a document is
// opened, so the Brain page itself stays light.
const RichMarkdownEditor = React.lazy(() => import('./brain/RichMarkdownEditor'));

/**
 * AntD's Tag never wraps or shrinks (`white-space: nowrap`, no max width), so a
 * long heading used as a tag pushed past the edge of its card. This cuts it
 * with "…" at the card's width; the full text is in the native tooltip.
 */
const ellipsisTagStyle: React.CSSProperties = {
  display: 'inline-block',
  maxWidth: '100%',
  boxSizing: 'border-box',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  verticalAlign: 'top',
};

const GRAPH_PREFERENCE_KEY = 'brain.graphOpen';

/** The graph panel starts open; only an explicit earlier close ('false') keeps it closed. */
function readGraphPreference(): boolean {
  try {
    return window.localStorage.getItem(GRAPH_PREFERENCE_KEY) !== 'false';
  } catch {
    return true;
  }
}

const DEFAULT_CONTENT = `# Nuevo documento\n\nEmpieza a escribir aquí...\n`;

// A file created a moment ago can briefly 404 if the read races the save (this
// mattered with the old GitHub store; it is a cheap safeguard now). Retry a
// couple of times before giving up.
const FETCH_RETRY_DELAYS_MS = [400, 1200];

async function fetchBrainFileWithRetry(path: string): ReturnType<typeof fetchBrainFile> {
  for (const delay of FETCH_RETRY_DELAYS_MS) {
    try {
      return await fetchBrainFile(path);
    } catch {
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
  return fetchBrainFile(path);
}

// ── Simple Markdown → HTML renderer ──────────────────────────────────────────


function renderMarkdownWithHighlight(md: string, entity: string): string {
  const OPEN = '\x00MO\x00';
  const CLOSE = '\x00MC\x00';
  const escaped = entity.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const marked = md.replace(new RegExp(escaped, 'gi'), (m) => `${OPEN}${m}${CLOSE}`);
  return renderMarkdown(marked)
    .split(OPEN).join('<mark style="background:#fff1b8;border-radius:2px;padding:0 1px">')
    .split(CLOSE).join('</mark>');
}

function toSlug(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-_]/g, '');
}

// ── Knowledge Graph helpers ───────────────────────────────────────────────────

interface GraphNode { id: string; label: string; name: string; }
interface GraphEdge { source: string; target: string; type: string; }
interface GraphData  { nodes: GraphNode[]; edges: GraphEdge[]; }

interface SearchChunk {
  path: string;
  text: string;
  metadata: Record<string, string>;
  score: number;
}
interface SearchResults {
  chunks: SearchChunk[];
  entities: { label: string; name: string; id: string }[];
  related_docs: string[];
}

const LABEL_COLORS: Record<string, string> = {
  Document:     '#1677ff',
  Organization: '#722ed1',
  Person:       '#eb2f96',
  Technology:   '#13c2c2',
  Process:      '#fa8c16',
  Concept:      '#52c41a',
  Topic:        '#faad14',
  Reference:    '#597ef7',
};
const labelColor = (l: string) => LABEL_COLORS[l] ?? '#8c8c8c';

// ── Obsidian-style circular node ─────────────────────────────────────────────

interface CircleNodeData {
  label: string; color: string; size: number; initials: string;
  /** `ring`: an entity on the ring, label pointing outward. `center`: a document, label underneath. */
  kind: 'ring' | 'center';
  angleDeg: number; flip: boolean;
}

const circleStyle = (data: CircleNodeData): React.CSSProperties => ({
  width: data.size, height: data.size, borderRadius: '50%',
  background: data.color,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
  border: '2px solid rgba(255,255,255,0.8)',
  boxSizing: 'border-box', flexShrink: 0,
});

/**
 * Edges anchor at the circle's centre (the edge component below trims them to
 * its rim), so these handles are invisible dots in the middle of the circle.
 */
const centerHandle = (top: string | number): React.CSSProperties => ({
  opacity: 0, width: 1, height: 1, top, left: '50%', transform: 'translate(-50%, -50%)', pointerEvents: 'none',
});

const CircleNode = ({ data }: NodeProps<CircleNodeData>) => {
  const initialsEl = (
    <span style={{ color: '#fff', fontSize: data.size > 44 ? 13 : 9, fontWeight: 700, userSelect: 'none' }}>
      {data.initials}
    </span>
  );

  if (data.kind === 'center') {
    // A document, in the middle of the ring: its name goes underneath.
    return (
      <div style={{ width: NODE_WIDTH, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, cursor: 'grab', position: 'relative' }}>
        <Handle type="target" position={Position.Top} style={centerHandle(data.size / 2)} />
        <Handle type="source" position={Position.Bottom} style={centerHandle(data.size / 2)} />
        <div style={circleStyle(data)}>{initialsEl}</div>
        <span
          title={data.label}
          style={{
            fontSize: 11, color: '#262626', width: '100%', textAlign: 'center', lineHeight: 1.3,
            overflow: 'hidden', overflowWrap: 'anywhere',
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          }}
        >
          {data.label}
        </span>
      </div>
    );
  }

  // An entity on the ring: the label points straight out along the radius, so
  // neighbours only need room for their circles, not for horizontal text. On the
  // left half it is turned 180° so the text is never upside down.
  const rotation = data.flip ? data.angleDeg + 180 : data.angleDeg;
  const reach = data.size / 2 + 8;
  return (
    <div style={{ position: 'relative', width: data.size, height: data.size, cursor: 'grab' }}>
      <Handle type="target" position={Position.Top} style={centerHandle('50%')} />
      <Handle type="source" position={Position.Bottom} style={centerHandle('50%')} />
      <div style={circleStyle(data)}>{initialsEl}</div>
      <div style={{ position: 'absolute', left: '50%', top: '50%', width: 0, height: 0, transform: `rotate(${rotation}deg)` }}>
        <span
          title={data.label}
          style={{
            position: 'absolute', top: -8, height: 16, lineHeight: '16px',
            fontSize: 11, color: '#262626', whiteSpace: 'nowrap',
            maxWidth: LABEL_REACH, overflow: 'hidden', textOverflow: 'ellipsis',
            ...(data.flip ? { right: reach, textAlign: 'right' as const } : { left: reach }),
          }}
        >
          {data.label}
        </span>
      </div>
    </div>
  );
};

// ── The tinted arc behind one entity type, with its title ───────────────────

const TYPE_TITLES: Record<string, string> = {
  Document: 'Documentos',
  Person: 'Personas',
  Organization: 'Organizaciones',
  Process: 'Procesos',
  Technology: 'Tecnologías',
  Concept: 'Conceptos',
  Topic: 'Temas',
  Reference: 'Documentos citados',
};

interface RingBandData {
  label: string; count: number; color: string;
  startAngle: number; endAngle: number; radius: number; extent: number;
  titleX: number; titleY: number;
}

const BAND_WIDTH = RING_NODE_SIZE + 26;

const RingBandNode = ({ data }: NodeProps<RingBandData>) => {
  const size = data.extent * 2;
  const point = (angle: number) => ({
    x: data.extent + data.radius * Math.sin(angle),
    y: data.extent - data.radius * Math.cos(angle),
  });
  const start = point(data.startAngle);
  const end = point(data.endAngle);
  const largeArc = data.endAngle - data.startAngle > Math.PI ? 1 : 0;
  return (
    <div style={{ width: size, height: size, position: 'relative', pointerEvents: 'none' }}>
      <svg width={size} height={size} style={{ position: 'absolute', inset: 0 }}>
        <path
          d={`M ${start.x} ${start.y} A ${data.radius} ${data.radius} 0 ${largeArc} 1 ${end.x} ${end.y}`}
          fill="none" stroke={data.color} strokeOpacity={0.14} strokeWidth={BAND_WIDTH} strokeLinecap="round"
        />
      </svg>
      <div style={{
        position: 'absolute', left: data.extent + data.titleX, top: data.extent + data.titleY,
        transform: 'translate(-50%, -50%)', whiteSpace: 'nowrap',
        padding: '3px 12px', borderRadius: 14, fontSize: 12, fontWeight: 600,
        color: data.color, background: `${data.color}1a`, border: `1px solid ${data.color}40`,
      }}>
        {TYPE_TITLES[data.label] ?? data.label}
        <span style={{ marginLeft: 6, fontWeight: 400, opacity: 0.7 }}>{data.count}</span>
      </div>
    </div>
  );
};

/**
 * A straight line between two circles, from rim to rim. ReactFlow's own edges
 * start at a handle; with the node's circle (and, for documents, a label under
 * it) that would leave the lines pointing at the wrong place.
 */
const RimEdge = ({ id, source, target, markerEnd, style, label, labelStyle, labelBgStyle }: EdgeProps) => {
  const sourceNode = useStore((state) => state.nodeInternals.get(source));
  const targetNode = useStore((state) => state.nodeInternals.get(target));
  if (!sourceNode || !targetNode) return null;

  const centreOf = (node: typeof sourceNode) => {
    const data = node.data as CircleNodeData;
    const position = node.positionAbsolute ?? node.position;
    return { x: position.x + (data.kind === 'center' ? NODE_WIDTH / 2 : data.size / 2), y: position.y + data.size / 2, radius: data.size / 2 };
  };
  const from = centreOf(sourceNode);
  const to = centreOf(targetNode);
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const length = Math.hypot(dx, dy) || 1;
  const [path, labelX, labelY] = getStraightPath({
    sourceX: from.x + (dx / length) * from.radius,
    sourceY: from.y + (dy / length) * from.radius,
    targetX: to.x - (dx / length) * (to.radius + 2),
    targetY: to.y - (dy / length) * (to.radius + 2),
  });
  return (
    <BaseEdge
      id={id} path={path} markerEnd={markerEnd} style={style}
      label={label} labelX={labelX} labelY={labelY} labelStyle={labelStyle} labelBgStyle={labelBgStyle}
    />
  );
};

const NODE_TYPES = { circle: CircleNode, ringBand: RingBandNode };
const EDGE_TYPES = { rim: RimEdge };

function initials(name: string): string {
  const words = name.trim().split(/\s+/);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

function buildRFNodes(nodes: GraphNode[], edges: GraphEdge[]): RFNode[] {
  const { nodes: placed, groups, radius, extent } = layoutGraph(nodes, edges);

  // One tinted arc per entity type: a single square node centred on the origin.
  const bands: RFNode[] = groups.map((group) => ({
    id: `band:${group.label}`,
    type: 'ringBand',
    position: { x: -extent, y: -extent },
    style: { width: extent * 2, height: extent * 2 },
    data: {
      label: group.label, count: group.count, color: labelColor(group.label),
      startAngle: group.startAngle, endAngle: group.endAngle, radius, extent,
      titleX: group.titleX, titleY: group.titleY,
    },
    draggable: false, selectable: false, focusable: false, zIndex: -1,
  }));

  const circles: RFNode[] = placed.map((node) => ({
    id: node.id,
    type: 'circle',
    // ReactFlow positions a node by its top-left corner; the layout gives circle centres.
    position: {
      x: node.cx - (node.kind === 'center' ? NODE_WIDTH / 2 : node.size / 2),
      y: node.cy - node.size / 2,
    },
    data: {
      label: node.name, color: labelColor(node.label), size: node.size, initials: initials(node.name),
      kind: node.kind, angleDeg: node.angleDeg, flip: node.flip,
    },
  }));

  return [...bands, ...circles];
}

// Lines are discreet until a node is selected: ~70 entities with a line each is
// noise, and what people look for is what is connected to the one they pick.
// Arrowheads and relation names appear only on the selected node's lines.
const MENTION_EDGE_OPACITY = 0.1;
const RELATION_EDGE_OPACITY = 0.2;
const DIRECTION_MARKER = { type: MarkerType.ArrowClosed, color: '#b37feb', width: 9, height: 9 };

function buildRFEdges(edges: GraphEdge[]): RFEdge[] {
  return edges.map((e, i) => {
    const isMentions = e.type === 'MENTIONS';
    return {
      id: `e${i}`,
      source: e.source, target: e.target,
      type: 'rim',
      data: { relation: isMentions ? '' : e.type },
      labelStyle: { fontSize: 10, fill: '#595959' },
      labelBgStyle: { fill: '#fff', fillOpacity: 0.9 },
      style: {
        stroke: isMentions ? '#bfbfbf' : '#b37feb',
        strokeWidth: isMentions ? 1 : 1.2,
        opacity: isMentions ? MENTION_EDGE_OPACITY : RELATION_EDGE_OPACITY,
      },
    };
  });
}

// ── Graph canvas with state (supports dragging + focus on click) ─────────────

interface GraphCanvasProps {
  data: GraphData;
  onEntitySelect: (name: string | null) => void;
}

// The canvas keeps its own node positions (they can be dragged), so it is built
// once per graph: callers give it `key={graphSignature(...)}` to rebuild it when
// the data behind it changes.
const GraphCanvas: React.FC<GraphCanvasProps> = ({ data, onEntitySelect }) => {
  const [nodes, , onNodesChange] = useNodesState(buildRFNodes(data.nodes, data.edges));
  const [edges] = useEdgesState(buildRFEdges(data.edges));
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const connectedIds = useMemo<Set<string>>(() => {
    if (!selectedId) return new Set();
    const ids = new Set<string>([selectedId]);
    data.edges.forEach(e => {
      if (e.source === selectedId) ids.add(e.target);
      if (e.target === selectedId) ids.add(e.source);
    });
    return ids;
  }, [selectedId, data.edges]);

  const displayNodes = useMemo(() => {
    if (!selectedId) return nodes;
    return nodes.map(n => (n.type === 'ringBand' ? n : {
      ...n,
      style: { ...n.style, opacity: connectedIds.has(n.id) ? 1 : 0.08, transition: 'opacity 0.2s' },
    }));
  }, [nodes, selectedId, connectedIds]);

  const displayEdges = useMemo(() => {
    if (!selectedId) return edges;
    return edges.map(e => {
      const active = e.source === selectedId || e.target === selectedId;
      const relation = (e.data as { relation?: string } | undefined)?.relation ?? '';
      return {
        ...e,
        // Only the selected node's lines are drawn, with the name and direction of the relation.
        label: active ? relation : undefined,
        markerEnd: active && relation !== '' ? DIRECTION_MARKER : undefined,
        style: { ...e.style, opacity: active ? 1 : 0.03, transition: 'opacity 0.2s' },
        animated: active && relation !== '',
      };
    });
  }, [edges, selectedId]);

  const handleNodeClick = useCallback((_: React.MouseEvent, node: RFNode) => {
    const isDoc = node.id.startsWith('doc:');
    const next = selectedId === node.id ? null : node.id;
    setSelectedId(next);
    if (!isDoc) {
      onEntitySelect(next ? (node.data as CircleNodeData).label : null);
    }
  }, [selectedId, onEntitySelect]);

  const handlePaneClick = useCallback(() => {
    setSelectedId(null);
    onEntitySelect(null);
  }, [onEntitySelect]);

  return (
    <ReactFlow
      nodes={displayNodes}
      edges={displayEdges}
      onNodesChange={onNodesChange}
      nodeTypes={NODE_TYPES}
      edgeTypes={EDGE_TYPES}
      onNodeClick={handleNodeClick}
      onPaneClick={handlePaneClick}
      fitView
      fitViewOptions={{ padding: 0.12 }}
      nodesDraggable
      nodesConnectable={false}
      elementsSelectable={false}
      minZoom={0.1}
      maxZoom={2}
    >
      <Background variant={BackgroundVariant.Dots} gap={24} size={1} color="#e8e8e8" />
      <Controls showInteractive={false} />
    </ReactFlow>
  );
};

// ── Collect all folder keys from tree ────────────────────────────────────────

function collectFolderKeys(nodes: BrainFile[]): Set<string> {
  const keys = new Set<string>();
  function walk(ns: BrainFile[]) {
    for (const n of ns) {
      if (n.type === 'folder') {
        keys.add(n.key);
        if (n.children) walk(n.children);
      }
    }
  }
  walk(nodes);
  return keys;
}

// ── Tree node title with hover-delete ─────────────────────────────────────────

type DeleteTarget = { key: string; type: 'file' | 'folder'; name: string };

interface TreeNodeTitleProps {
  label: string;
  nodeKey: string;
  isLeaf: boolean;
  canEdit: boolean;
  onDeleteRequest: (t: DeleteTarget) => void;
}

const TreeNodeTitle: React.FC<TreeNodeTitleProps> = ({ label, nodeKey, isLeaf, canEdit, onDeleteRequest }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <span
      style={{ display: 'flex', alignItems: 'center', minWidth: 0, width: '100%' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* A long name is cut with "…" so the delete button always has room; the
          full name is in the native tooltip. */}
      <span
        title={label}
        style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
      >
        {label}
      </span>
      {canEdit && (
        // Its space is always reserved (only the visibility changes on hover), so
        // names do not re-flow, or change where they are cut, as the mouse moves.
        <DeleteOutlined
          aria-label={`Eliminar ${label}`}
          style={{
            fontSize: 11,
            color: '#ff4d4f',
            paddingLeft: 6,
            flexShrink: 0,
            opacity: hovered ? 1 : 0,
            pointerEvents: hovered ? 'auto' : 'none',
            transition: 'opacity 0.12s',
          }}
          onClick={(e) => {
            e.stopPropagation();
            onDeleteRequest({ key: nodeKey, type: isLeaf ? 'file' : 'folder', name: label });
          }}
        />
      )}
    </span>
  );
};

// ── Entity mentions side panel (per-document and global graph) ──────────────

interface Mention { path: string; text: string; metadata: Record<string, string>; index: number }

interface MentionsPanelProps {
  entity: string;
  label?: string;
  mentions: Mention[];
  loading: boolean;
  /** Global graph: tag each fragment with the document it comes from. */
  showDocName?: boolean;
  /** `side`: a column to the right of the graph. `stacked`: a band under it, for a narrow panel. */
  orientation?: 'side' | 'stacked';
}

const MentionsPanel: React.FC<MentionsPanelProps> = ({ entity, label, mentions, loading, showDocName = false, orientation = 'side' }) => (
  <div
    style={{
      ...(orientation === 'side'
        ? { width: 300, borderLeft: glassDivider }
        : { width: '100%', maxHeight: '48%', flexShrink: 0, borderTop: glassDivider }),
      background: glass.surface, display: 'flex', flexDirection: 'column', overflow: 'hidden',
    }}
  >
    <div style={{ ...glassBarStyle, padding: '10px 14px', gap: 8 }}>
      <div style={{ width: 10, height: 10, borderRadius: '50%', background: labelColor(label ?? 'Concept'), flexShrink: 0 }} />
      <Text strong style={{ fontSize: 13, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entity}</Text>
      <Text style={{ fontSize: 11, color: '#8c8c8c' }}>{label}</Text>
    </div>
    <div style={{ flex: 1, overflow: 'auto', padding: '10px 12px' }}>
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 24 }}><Spin size="small" /></div>
      ) : mentions.length === 0 ? (
        <Text style={{ fontSize: 12, color: '#bfbfbf', display: 'block', textAlign: 'center', paddingTop: 24 }}>
          {showDocName ? 'Sin fragmentos' : 'No se encontraron fragmentos'}
        </Text>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {mentions.map((m, i) => {
            const headers = [m.metadata.h1, m.metadata.h2, m.metadata.h3].filter(Boolean);
            const docName = m.path.split('/').pop()?.replace('.md', '') ?? m.path;
            const headerTagStyle = showDocName
              ? { background: '#f9f0ff', color: '#722ed1', border: '1px solid #efdbff' }
              : { background: '#f0f5ff', color: '#1677ff', border: '1px solid #d6e4ff' };
            return (
              <div key={i} style={{ ...glassInsetStyle, padding: '8px 10px' }}>
                {showDocName && (
                  <Tag title={docName} style={{ ...ellipsisTagStyle, fontSize: 10, margin: '0 0 5px', background: '#f0f5ff', color: '#1677ff', border: '1px solid #d6e4ff', borderRadius: 4 }}>
                    {docName}
                  </Tag>
                )}
                {headers.length > 0 && (
                  <div style={{ marginBottom: showDocName ? 5 : 6, display: 'flex', gap: 4, flexWrap: 'wrap', minWidth: 0 }}>
                    {headers.map((h, j) => <Tag key={j} title={h} style={{ ...ellipsisTagStyle, fontSize: 10, margin: 0, borderRadius: 4, ...headerTagStyle }}>{h}</Tag>)}
                  </div>
                )}
                <div
                  style={{ fontSize: 12, color: '#434343', lineHeight: 1.6 }}
                  dangerouslySetInnerHTML={{ __html: renderMarkdownWithHighlight(m.text, entity) }}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  </div>
);

// ── Document graph body (shared by the side panel and the full-screen view) ──

interface DocumentGraphBodyProps {
  loading: boolean;
  data: GraphData | null;
  selectedEntity: string | null;
  mentions: Mention[];
  mentionsLoading: boolean;
  onEntitySelect: (name: string | null) => void;
  /** `stacked`: mentions as a band under the graph (narrow panel). `side`: a column beside it (full screen). */
  layout: 'stacked' | 'side';
}

const DocumentGraphBody: React.FC<DocumentGraphBodyProps> = ({
  loading, data, selectedEntity, mentions, mentionsLoading, onEntitySelect, layout,
}) => {
  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
        <Spin tip="Cargando grafo..." />
      </div>
    );
  }
  if (!data || data.nodes.length === 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: 8, padding: 24, textAlign: 'center' }}>
        <ApartmentOutlined style={{ fontSize: 32, color: '#d9d9d9' }} />
        <span style={{ color: '#8c8c8c', fontSize: 13 }}>
          Este documento aún no tiene grafo. Guárdalo para indexarlo.
        </span>
      </div>
    );
  }
  return (
    <div style={{ flex: 1, minHeight: 0, minWidth: 0, display: 'flex', flexDirection: layout === 'stacked' ? 'column' : 'row' }}>
      <div style={{ flex: 1, minHeight: 0, minWidth: 0, position: 'relative' }}>
        <GraphCanvas
          key={graphSignature(data.nodes, data.edges)}
          data={data}
          onEntitySelect={onEntitySelect}
        />
      </div>
      {selectedEntity && (
        <MentionsPanel
          orientation={layout === 'stacked' ? 'stacked' : 'side'}
          entity={selectedEntity}
          label={data.nodes.find(n => n.name === selectedEntity)?.label}
          mentions={mentions}
          loading={mentionsLoading}
        />
      )}
    </div>
  );
};

// ── Tree helpers ──────────────────────────────────────────────────────────────

function toTreeDataNode(node: BrainFile): TreeDataNode {
  return {
    key: node.key,
    title: node.title,
    icon: node.type === 'folder'
      ? <FolderOutlined style={{ color: '#fa8c16' }} />
      : <FileMarkdownOutlined style={{ color: '#1677ff' }} />,
    children: node.children?.map(toTreeDataNode),
    isLeaf: node.type === 'file',
  };
}

// ── Component ─────────────────────────────────────────────────────────────────

const BrainPage: React.FC = () => {
  const { canEdit } = useUser();

  const [tree, setTree] = useState<BrainFile[]>([]);
  const [treeLoading, setTreeLoading] = useState(true);
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);

  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [currentFolder, setCurrentFolder] = useState<string | null>(null);
  const [currentSha, setCurrentSha] = useState<string | null>(null);
  const [content, setContent] = useState('');
  const [fileLoading, setFileLoading] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  // edit = visual editor (read-only for viewers), source = the raw Markdown, graph = knowledge graph
  const [mode, setMode] = useState<'edit' | 'source'>('edit');
  // The knowledge graph is a panel BESIDE the editor, not a mode that replaces it.
  // Open by default; once the user closes it, that is remembered.
  const [graphOpen, setGraphOpen] = useState<boolean>(readGraphPreference);
  const [graphExpanded, setGraphExpanded] = useState(false);
  useEffect(() => {
    try { window.localStorage.setItem(GRAPH_PREFERENCE_KEY, String(graphOpen)); } catch { /* storage may be blocked */ }
  }, [graphOpen]);
  // The visual editor is uncontrolled once mounted: bumping this remounts it
  // with the current `content` (a different document, or coming back from the
  // raw Markdown view).
  const [editorVersion, setEditorVersion] = useState(0);
  const [graphData, setGraphData] = useState<GraphData | null>(null);
  const [graphLoading, setGraphLoading] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState<string | null>(null);
  const [mentions, setMentions] = useState<Mention[]>([]);
  const [mentionsLoading, setMentionsLoading] = useState(false);

  const [searchMode, setSearchMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResults | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);

  const [globalGraphMode, setGlobalGraphMode] = useState(false);
  const [globalGraphData, setGlobalGraphData] = useState<GraphData | null>(null);
  const [globalGraphLoading, setGlobalGraphLoading] = useState(false);

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [deletingNode, setDeletingNode] = useState(false);

  const [folderModalOpen, setFolderModalOpen] = useState(false);
  const [fileModalOpen, setFileModalOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFileName, setNewFileName] = useState('');
  const [modalSaving, setModalSaving] = useState(false);

  // Saving/deleting only waits for the save. Indexing (chunking,
  // embeddings, graph) runs in the background, tracked by BrainIndexingProvider,
  // which notifies the user when it ends — so these flags are short-lived and
  // only keep two saves from overlapping.
  const journeyBusy = saving || deleting || deletingNode || modalSaving;

  const folderKeys = useMemo(() => collectFolderKeys(tree), [tree]);

  // ── "Updating the Brain" notice ─────────────────────────────────────────────
  // A brief heads-up right after the save. It does not wait for anything: it
  // closes by itself (or with "Entendido") while the update keeps going.

  const organization = useActiveOrganization();
  const { trackIndexing, finished: indexingFinished } = useBrainIndexing();
  const [notice, setNotice] = useState<{ open: boolean; label: string } | null>(null);
  const noticeTimer = useRef<number | undefined>(undefined);

  const closeNotice = useCallback(() => {
    if (noticeTimer.current) window.clearTimeout(noticeTimer.current);
    setNotice((current) => (current ? { ...current, open: false } : current));
  }, []);

  useEffect(() => () => {
    if (noticeTimer.current) window.clearTimeout(noticeTimer.current);
  }, []);

  /** The change is saved: tell the user, and index it in the background. */
  const announceUpdate = useCallback((spec: IndexingJobSpec) => {
    if (noticeTimer.current) window.clearTimeout(noticeTimer.current);
    setNotice({ open: true, label: spec.label });
    noticeTimer.current = window.setTimeout(closeNotice, 6000);
    trackIndexing(spec);
  }, [closeNotice, trackIndexing]);

  // ── Load tree ───────────────────────────────────────────────────────────────

  const loadTree = useCallback(async () => {
    setTreeLoading(true);
    try {
      const files = await fetchBrainTree();
      setTree(files);
      setExpandedKeys(files.filter((f) => f.type === 'folder').map((f) => f.key));
    } catch {
      message.error('No se pudo cargar el Brain. Inténtalo de nuevo en unos minutos.');
    } finally {
      setTreeLoading(false);
    }
  }, []);

  useEffect(() => { void loadTree(); }, [loadTree]);

  // ── Select node ─────────────────────────────────────────────────────────────

  const handleSelect = async (keys: React.Key[]) => {
    const key = keys[0] as string;
    if (!key) return;

    if (folderKeys.has(key)) {
      setCurrentFolder(key);
      return;
    }

    // File clicked — exit search/global-graph modes
    setSearchMode(false);
    setGlobalGraphMode(false);

    const parentFolder = key.includes('/') ? key.split('/').slice(0, -1).join('/') : null;
    setCurrentFolder(parentFolder);

    if (key === selectedFile) return;
    setFileLoading(true);
    setSelectedFile(key);
    setMode('edit');
    try {
      const { content: fileContent, sha } = await fetchBrainFile(key);
      setContent(fileContent);
      setCurrentSha(sha);
      setIsDirty(false);
      setEditorVersion((v) => v + 1);
    } catch {
      message.error('No se pudo cargar el archivo');
      setSelectedFile(null);
    } finally {
      setFileLoading(false);
    }
  };

  // ── Save ────────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!selectedFile || journeyBusy) return;
    setSaving(true);
    const path = selectedFile;
    try {
      const { sha } = await saveBrainFile(path, content, currentSha ?? undefined, { deferIndexing: true });
      if (sha) setCurrentSha(sha);
      setIsDirty(false);
      announceUpdate({ action: 'index', label: path.split('/').pop() ?? path, paths: [path], sha });
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  // ── Delete file ─────────────────────────────────────────────────────────────

  const handleDelete = async () => {
    if (!selectedFile || !currentSha || journeyBusy) return;
    setDeleting(true);
    const path = selectedFile;
    try {
      await deleteBrainFile(path, currentSha, { deferIndexing: true });
      announceUpdate({ action: 'deindex', label: path.split('/').pop() ?? path, paths: [path] });
      setSelectedFile(null);
      setCurrentFolder(null);
      setContent('');
      setCurrentSha(null);
      await loadTree();
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Error al eliminar');
    } finally {
      setDeleting(false);
    }
  };

  // ── Delete node from tree (file or folder) ──────────────────────────────────

  const handleDeleteNode = async () => {
    if (!deleteTarget || journeyBusy) return;
    setDeletingNode(true);
    try {
      const { deletedPaths } = await deleteBrainPath(deleteTarget.key, { deferIndexing: true });
      // Only the documents that really left the repo have anything to remove from the index.
      if (deletedPaths.length > 0) {
        announceUpdate({ action: 'deindex', label: deleteTarget.name, paths: deletedPaths });
      }
      setDeleteTarget(null);
      // Clear editor if the open file was deleted or lived inside a deleted folder
      if (selectedFile && (selectedFile === deleteTarget.key || selectedFile.startsWith(`${deleteTarget.key}/`))) {
        setSelectedFile(null);
        setCurrentFolder(null);
        setContent('');
        setCurrentSha(null);
        setIsDirty(false);
      }
      await loadTree();
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Error al eliminar');
    } finally {
      setDeletingNode(false);
    }
  };

  // ── Create folder ───────────────────────────────────────────────────────────

  const handleCreateFolder = async () => {
    const name = newFolderName.trim();
    if (!name || journeyBusy) return;
    const slug = toSlug(name);
    const path = currentFolder ? `${currentFolder}/${slug}/README.md` : `${slug}/README.md`;
    setModalSaving(true);
    try {
      const { sha } = await saveBrainFile(path, `# ${name}\n`, undefined, { deferIndexing: true });
      setFolderModalOpen(false);
      setNewFolderName('');
      announceUpdate({ action: 'index', label: name, paths: [path], sha });
      await loadTree();
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Error al crear carpeta');
    } finally {
      setModalSaving(false);
    }
  };

  // ── Create file ─────────────────────────────────────────────────────────────

  const handleCreateFile = async () => {
    const name = newFileName.trim();
    if (!name || journeyBusy) return;
    const slug = toSlug(name);
    if (!slug) { message.error('El nombre contiene solo caracteres especiales'); return; }
    const path = currentFolder ? `${currentFolder}/${slug}.md` : `${slug}.md`;
    setModalSaving(true);
    try {
      const { sha } = await saveBrainFile(path, DEFAULT_CONTENT, undefined, { deferIndexing: true });
      setFileModalOpen(false);
      setNewFileName('');
      announceUpdate({ action: 'index', label: name, paths: [path], sha });
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Error al crear documento');
      setModalSaving(false);
      return;
    }
    // The document is saved (and being indexed in the background). Opening it in the editor is a
    // convenience: if that refresh fails it must not be reported as a failed
    // creation (the user would retry and hit a duplicate-file conflict).
    try {
      const [, { content: fc, sha }] = await Promise.all([
        loadTree(),
        fetchBrainFileWithRetry(path),
      ]);
      setSelectedFile(path);
      setContent(fc);
      setCurrentSha(sha);
      setIsDirty(false);
      setMode('edit');
      setEditorVersion((v) => v + 1);
    } catch {
      await loadTree();
      message.warning('Documento creado, pero no se pudo abrir. Ábrelo desde el árbol.');
    } finally {
      setModalSaving(false);
    }
  };

  // ── Load graph ──────────────────────────────────────────────────────────────

  // Only the latest request may touch the graph state: a slow answer for the
  // previous document must not overwrite the one the user is looking at.
  const graphRequest = useRef(0);
  const loadGraph = useCallback(async (path: string, options: { refresh?: boolean } = {}) => {
    const requestId = ++graphRequest.current;
    // A refresh (after an indexing finished) keeps the current graph on screen
    // while the new one loads, instead of flashing a spinner over the user's work.
    if (!options.refresh) {
      setGraphLoading(true);
      setGraphData(null);
    }
    try {
      const res = await fetch(`${EXECUTOR_API}/brain/graph?path=${encodeURIComponent(path)}`, {
        headers: buildAuthHeaders(),
      });
      if (!res.ok) throw new Error('Error fetching graph');
      const data: GraphData = normalizeGraph(await res.json() as GraphData);
      if (requestId === graphRequest.current) setGraphData(data);
    } catch {
      if (requestId === graphRequest.current && !options.refresh) message.error('No se pudo cargar el grafo');
    } finally {
      if (requestId === graphRequest.current) setGraphLoading(false);
    }
  }, []);

  // Open graph: follow the document being edited…
  useEffect(() => {
    if (!graphOpen || !selectedFile) return;
    setSelectedEntity(null);
    setMentions([]);
    void loadGraph(selectedFile);
  }, [graphOpen, selectedFile, loadGraph]);

  // …and refresh it when a background indexing finishes (the graph is what it produced).
  const firstFinished = useRef(indexingFinished);
  useEffect(() => {
    if (indexingFinished === firstFinished.current) return;
    firstFinished.current = indexingFinished;
    if (graphOpen && selectedFile) void loadGraph(selectedFile, { refresh: true });
  }, [indexingFinished, graphOpen, selectedFile, loadGraph]);

  // ── Semantic search ─────────────────────────────────────────────────────────

  const handleSearch = useCallback(async (q: string) => {
    if (!q.trim()) return;
    setSearchLoading(true);
    setSearchResults(null);
    try {
      const res = await fetch(`${EXECUTOR_API}/brain/query`, {
        method: 'POST',
        headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ question: q, top_k: 8 }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json() as SearchResults;
      setSearchResults(data);
    } catch {
      message.error('Error en la búsqueda semántica');
    } finally {
      setSearchLoading(false);
    }
  }, []);

  // ── Global graph ─────────────────────────────────────────────────────────────

  const loadGlobalGraph = useCallback(async () => {
    setGlobalGraphLoading(true);
    setGlobalGraphData(null);
    setGlobalGraphMode(true);
    setSelectedFile(null);
    setSelectedEntity(null);
    setMentions([]);
    try {
      const res = await fetch(`${EXECUTOR_API}/brain/graph/global`, {
        headers: buildAuthHeaders(),
      });
      if (!res.ok) throw new Error();
      const data = normalizeGraph(await res.json() as GraphData);
      setGlobalGraphData(data);
    } catch {
      message.error('No se pudo cargar el grafo global');
    } finally {
      setGlobalGraphLoading(false);
    }
  }, []);

  // ── Entity select → fetch mentions ─────────────────────────────────────────

  const handleEntitySelect = useCallback(async (entityName: string | null) => {
    setSelectedEntity(entityName);
    setMentions([]);
    if (!entityName) return;
    setMentionsLoading(true);
    try {
      const pathParam = selectedFile ? `&path=${encodeURIComponent(selectedFile)}` : '';
      const res = await fetch(
        `${EXECUTOR_API}/brain/mentions?entity=${encodeURIComponent(entityName)}${pathParam}`,
        { headers: buildAuthHeaders() }
      );
      if (!res.ok) throw new Error();
      const data = await res.json() as { mentions: { path: string; text: string; metadata: Record<string, string>; index: number }[] };
      setMentions(data.mentions);
    } catch {
      // silent
    } finally {
      setMentionsLoading(false);
    }
  }, [selectedFile]);

  // ── Breadcrumb ───────────────────────────────────────────────────────────────

  const breadcrumbItems = selectedFile
    ? selectedFile.split('/').map((part, i, arr) => ({
        title: <Text style={{ fontSize: 13, color: i === arr.length - 1 ? '#262626' : '#8c8c8c' }}>
          {part.replace('.md', '')}
        </Text>,
      }))
    : [];

  const treeData = useMemo(() => tree.map(toTreeDataNode), [tree]);

  const contextLabel = currentFolder
    ? <Text code style={{ fontSize: 11 }}>{currentFolder}</Text>
    : <Text style={{ fontSize: 11, color: '#8c8c8c' }}>raíz</Text>;

  return (
    <>
      <GlassWorkspace>

        {/* ── Sidebar ── */}
        <GlassPanel width={260}>
          {/* Header */}
          <div style={{ ...glassBarStyle, padding: '12px 16px', justifyContent: 'flex-end' }}>
            <Space size={2}>
              <Tooltip title="Búsqueda semántica">
                <Button
                  type={searchMode ? 'primary' : 'text'} size="small" icon={<SearchOutlined />}
                  onClick={() => { setSearchMode(m => !m); setSearchResults(null); setSearchQuery(''); }}
                />
              </Tooltip>
              <Tooltip title="Grafo global">
                <Button
                  type={globalGraphMode ? 'primary' : 'text'} size="small" icon={<GlobalOutlined />}
                  onClick={() => void loadGlobalGraph()}
                />
              </Tooltip>
              <Tooltip title="Recargar">
                <Button type="text" size="small" icon={<ReloadOutlined />} onClick={() => void loadTree()} />
              </Tooltip>
              {canEdit && (
                <Tooltip title={`Nueva carpeta en ${currentFolder ?? 'raíz'}`}>
                  <Button type="text" size="small" icon={<FolderAddOutlined />} disabled={journeyBusy} onClick={() => setFolderModalOpen(true)} />
                </Tooltip>
              )}
              {canEdit && (
                <Tooltip title={`Nuevo archivo en ${currentFolder ?? 'raíz'}`}>
                  <Button type="text" size="small" icon={<FileAddOutlined />} disabled={journeyBusy} onClick={() => setFileModalOpen(true)} />
                </Tooltip>
              )}
            </Space>
          </div>

          {/* Where new files and folders will be created */}
          <div style={{ ...glassBarStyle, padding: '5px 16px', background: glass.surface }}>
            <Space size={4}>
              <Text style={{ fontSize: 10, color: '#8c8c8c' }}>en:</Text>
              {contextLabel}
            </Space>
          </div>

          {/* Tree / Search */}
          <div className="brain-tree" style={{ padding: searchMode ? '8px 12px' : '8px 4px', overflow: 'auto', flex: 1, minHeight: 0 }}>
            {searchMode ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <Input.Search
                  placeholder="Buscar en el Brain..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  onSearch={(v) => void handleSearch(v)}
                  loading={searchLoading}
                  autoFocus
                  size="small"
                  allowClear
                  onClear={() => setSearchResults(null)}
                />
                {searchLoading && (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: 16 }}>
                    <Spin size="small" />
                  </div>
                )}
                {searchResults && !searchLoading && searchResults.chunks.length === 0 && (
                  <Text style={{ fontSize: 12, color: '#bfbfbf', textAlign: 'center', display: 'block', padding: '16px 0' }}>
                    Sin resultados
                  </Text>
                )}
                {searchResults?.chunks.map((chunk, i) => {
                  const headers = [chunk.metadata.h1, chunk.metadata.h2, chunk.metadata.h3].filter(Boolean);
                  const fileName = chunk.path.split('/').pop()?.replace('.md', '') ?? chunk.path;
                  return (
                    <div
                      key={i}
                      onClick={() => void handleSelect([chunk.path])}
                      style={{
                        ...glassInsetStyle,
                        padding: '8px 10px', cursor: 'pointer',
                        minWidth: 0, overflow: 'hidden',
                      }}
                      onMouseEnter={e => (e.currentTarget.style.borderColor = colors.primary)}
                      onMouseLeave={e => (e.currentTarget.style.borderColor = glass.border)}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, marginBottom: 4 }}>
                        <Text strong style={{ fontSize: 12, color: '#1677ff', minWidth: 0, overflowWrap: 'anywhere' }}>{fileName}</Text>
                        <Tag style={{ fontSize: 10, margin: 0, borderRadius: 10, flexShrink: 0 }}>{(chunk.score * 100).toFixed(0)}%</Tag>
                      </div>
                      {headers.length > 0 && (
                        <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', marginBottom: 4, minWidth: 0 }}>
                          {headers.map((h, j) => (
                            <Tag key={j} title={h} style={{ ...ellipsisTagStyle, fontSize: 10, margin: 0, background: '#f0f5ff', color: '#1677ff', border: '1px solid #d6e4ff', borderRadius: 4 }}>{h}</Tag>
                          ))}
                        </div>
                      )}
                      <Text style={{ fontSize: 11, color: '#595959', lineHeight: 1.5, overflowWrap: 'anywhere' }}>
                        {chunk.text.slice(0, 120)}{chunk.text.length > 120 ? '…' : ''}
                      </Text>
                    </div>
                  );
                })}
                {searchResults && searchResults.related_docs.length > 0 && (
                  <div style={{ borderTop: glassDivider, paddingTop: 8, marginTop: 4 }}>
                    <Text style={{ fontSize: 11, color: '#8c8c8c' }}>Documentos relacionados:</Text>
                    {searchResults.related_docs.map((p, i) => (
                      <div key={i} onClick={() => void handleSelect([p])} style={{ cursor: 'pointer', padding: '3px 0' }}>
                        <Text style={{ fontSize: 11, color: '#1677ff' }}>{p.split('/').pop()?.replace('.md', '')}</Text>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : treeLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
                <Spin size="small" />
              </div>
            ) : tree.length === 0 ? (
              <div style={{ padding: '24px 16px', textAlign: 'center' }}>
                <Text style={{ color: '#bfbfbf', fontSize: 12 }}>
                  Repo vacío. Crea la primera carpeta o archivo.
                </Text>
              </div>
            ) : (
              <Tree
                showIcon
                // Rows take the panel's width (AntD otherwise sizes each row to
                // its name), which is what lets long names be cut with "…".
                blockNode
                treeData={treeData}
                expandedKeys={expandedKeys}
                selectedKeys={selectedFile ? [selectedFile] : currentFolder ? [currentFolder] : []}
                onExpand={(keys) => setExpandedKeys(keys)}
                onSelect={(keys) => void handleSelect(keys)}
                style={{ fontSize: 13 }}
                titleRender={(node) => (
                  <TreeNodeTitle
                    label={String(node.title)}
                    nodeKey={String(node.key)}
                    isLeaf={node.isLeaf ?? false}
                    canEdit={canEdit && !journeyBusy}
                    onDeleteRequest={setDeleteTarget}
                  />
                )}
              />
            )}
          </div>
        </GlassPanel>

        {/* ── Main Content ── */}
        <GlassPanel>
          {selectedFile ? (
            <>
              <div style={{ ...glassBarStyle, padding: '8px 20px', gap: 12 }}>
                <div className="brain-doc-breadcrumb">
                  <Breadcrumb style={{ fontSize: 13 }} items={breadcrumbItems} />
                </div>
                {isDirty && <Tag color="orange" style={{ fontSize: 11, margin: 0, flexShrink: 0 }}>Sin guardar</Tag>}
                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                  <Segmented
                    size="small"
                    value={mode}
                    onChange={(v) => {
                      const next = v as 'edit' | 'source';
                      // Coming back to the visual editor: reload it from the current text.
                      if (next === 'edit') setEditorVersion((n) => n + 1);
                      setMode(next);
                    }}
                    options={[
                      // The visual editor IS the formatted view, so there is no separate preview.
                      { label: canEdit ? 'Editar' : 'Documento', value: 'edit', icon: canEdit ? <EditOutlined /> : <EyeOutlined /> },
                      { label: 'Markdown', value: 'source', icon: <CodeOutlined /> },
                    ]}
                  />
                  <Button
                    size="small"
                    type={graphOpen ? 'primary' : 'default'}
                    icon={<ApartmentOutlined />}
                    aria-pressed={graphOpen}
                    onClick={() => {
                      if (graphOpen) { setSelectedEntity(null); setMentions([]); }
                      setGraphOpen((open) => !open);
                    }}
                  >
                    Grafo
                  </Button>
                  {canEdit && (
                    <>
                      <Popconfirm
                        title="¿Eliminar este documento?"
                        description="Esta acción no se puede deshacer."
                        icon={<ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />}
                        okText="Eliminar" okButtonProps={{ danger: true }}
                        cancelText="Cancelar"
                        onConfirm={() => void handleDelete()}
                      >
                        <Button size="small" danger icon={<DeleteOutlined />} loading={deleting} disabled={journeyBusy && !deleting} />
                      </Popconfirm>
                      <Button
                        type="primary" size="small" icon={<SaveOutlined />}
                        loading={saving} disabled={!isDirty || (journeyBusy && !saving)}
                        onClick={() => void handleSave()}
                      >
                        Guardar
                      </Button>
                    </>
                  )}
                </div>
              </div>

              <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
                <div style={{ flex: 1, minWidth: 0, overflow: 'auto', position: 'relative' }}>
                {fileLoading ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <Spin />
                  </div>
                ) : mode === 'edit' ? (
                  <Suspense
                    fallback={
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <Spin />
                      </div>
                    }
                  >
                    <RichMarkdownEditor
                      key={`${selectedFile}:${editorVersion}`}
                      markdown={content}
                      readOnly={!canEdit}
                      onChange={(next) => { setContent(next); setIsDirty(true); }}
                      onUnsupported={() => {
                        // Never lose or mangle a document the visual editor cannot represent:
                        // show it as the raw Markdown it is.
                        setMode('source');
                        message.warning('Este documento usa Markdown que el editor visual no admite. Se muestra en modo Markdown.');
                      }}
                    />
                  </Suspense>
                ) : mode === 'source' ? (
                  <TextArea
                    value={content}
                    readOnly={!canEdit}
                    onChange={(e) => { setContent(e.target.value); setIsDirty(true); }}
                    style={{
                      height: '100%', border: 'none', borderRadius: 0,
                      resize: 'none',
                      fontFamily: '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
                      fontSize: 13, lineHeight: 1.8, padding: '28px 36px',
                      outline: 'none', boxShadow: 'none',
                      background: 'transparent', color: '#262626',
                    }}
                  />
                ) : null}
                </div>

                {/* ── Knowledge graph of this document, beside the editor ── */}
                {graphOpen && (
                  <aside
                    aria-label="Grafo del documento"
                    style={{
                      width: 'min(460px, 45%)', flexShrink: 0, minWidth: 0,
                      borderLeft: glassDivider, display: 'flex', flexDirection: 'column', overflow: 'hidden',
                    }}
                  >
                    <div style={{ ...glassBarStyle, padding: '8px 14px', gap: 8 }}>
                      <ApartmentOutlined style={{ color: '#722ed1' }} />
                      <Text strong style={{ fontSize: 13, flex: 1 }}>Grafo del documento</Text>
                      <Tooltip title="Ver a pantalla completa">
                        <Button
                          type="text" size="small" aria-label="Expandir el grafo a pantalla completa"
                          icon={<FullscreenOutlined />}
                          onClick={() => { setSelectedEntity(null); setMentions([]); setGraphExpanded(true); }}
                        />
                      </Tooltip>
                      <Button
                        type="text" size="small" aria-label="Cerrar el grafo"
                        icon={<CloseOutlined />}
                        onClick={() => { setGraphOpen(false); setSelectedEntity(null); setMentions([]); }}
                      />
                    </div>
                    <DocumentGraphBody
                      layout="stacked"
                      loading={graphLoading}
                      data={graphData}
                      selectedEntity={selectedEntity}
                      mentions={mentions}
                      mentionsLoading={mentionsLoading}
                      onEntitySelect={(name) => void handleEntitySelect(name)}
                    />
                  </aside>
                )}
              </div>
            </>
          ) : globalGraphMode ? (
            /* ── Global graph view ── */
            <>
              <div style={{ ...glassBarStyle, padding: '8px 20px', gap: 12 }}>
                <Button
                  type="text" size="small" icon={<ArrowLeftOutlined />}
                  onClick={() => { setGlobalGraphMode(false); setSelectedEntity(null); setMentions([]); }}
                >
                  Volver
                </Button>
                <Space size={6}>
                  <GlobalOutlined style={{ color: '#722ed1' }} />
                  <Text strong style={{ fontSize: 13 }}>Grafo de conocimiento global</Text>
                </Space>
                {globalGraphData && (
                  <Text style={{ fontSize: 12, color: '#8c8c8c', marginLeft: 'auto' }}>
                    {globalGraphData.nodes.filter(n => n.label === 'Document').length} docs ·{' '}
                    {globalGraphData.nodes.filter(n => n.label !== 'Document').length} entidades
                  </Text>
                )}
              </div>
              <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
                {globalGraphLoading ? (
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Spin tip="Cargando grafo global..." />
                  </div>
                ) : !globalGraphData || globalGraphData.nodes.length === 0 ? (
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                    <GlobalOutlined style={{ fontSize: 32, color: '#d9d9d9' }} />
                    <Text style={{ color: '#8c8c8c', fontSize: 13 }}>No hay documentos indexados aún.</Text>
                  </div>
                ) : (
                  <>
                    <div style={{ flex: 1, position: 'relative', minWidth: 0 }}>
                      <div style={{ position: 'absolute', top: 12, left: 12, zIndex: 10, display: 'flex', gap: 6, flexWrap: 'wrap', maxWidth: 320 }}>
                        {[...new Set(globalGraphData.nodes.map(n => n.label))].map(label => (
                          <Tag key={label} style={{ background: labelColor(label), color: '#fff', border: 'none', fontSize: 11, borderRadius: 20 }}>{label}</Tag>
                        ))}
                      </div>
                      <GraphCanvas
                        key={graphSignature(globalGraphData.nodes, globalGraphData.edges)}
                        data={globalGraphData}
                        onEntitySelect={(name) => void handleEntitySelect(name)}
                      />
                    </div>
                    {selectedEntity && (
                      <MentionsPanel
                        entity={selectedEntity}
                        label={globalGraphData.nodes.find(n => n.name === selectedEntity)?.label}
                        mentions={mentions}
                        loading={mentionsLoading}
                        showDocName
                      />
                    )}
                  </>
                )}
              </div>
            </>
          ) : (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {treeLoading ? <Spin size="large" /> : (
                <Empty
                  description={
                    <Space direction="vertical" size={4} style={{ textAlign: 'center' }}>
                      <Text style={{ color: '#8c8c8c', fontSize: 14 }}>
                        Selecciona un documento para empezar
                      </Text>
                      <Text style={{ color: '#bfbfbf', fontSize: 12 }}>
                        o crea una carpeta <FolderAddOutlined /> / archivo <FileAddOutlined /> en el panel lateral
                      </Text>
                    </Space>
                  }
                />
              )}
            </div>
          )}
        </GlassPanel>
      </GlassWorkspace>

      {/* ── Delete confirmation modal ── */}
      <Modal
        title={
          <Space>
            <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
            {deleteTarget?.type === 'folder' ? 'Eliminar carpeta' : 'Eliminar archivo'}
          </Space>
        }
        open={!!deleteTarget}
        okText="Eliminar"
        okButtonProps={{ danger: true, loading: deletingNode, disabled: journeyBusy && !deletingNode }}
        cancelText="Cancelar"
        onOk={() => void handleDeleteNode()}
        onCancel={() => setDeleteTarget(null)}
      >
        {deleteTarget?.type === 'folder' ? (
          <Space direction="vertical" size={4}>
            <Typography.Text>
              ¿Eliminar la carpeta <Typography.Text strong>"{deleteTarget.name}"</Typography.Text>?
            </Typography.Text>
            <Typography.Text type="danger" style={{ fontSize: 12 }}>
              Se eliminarán todos los archivos que contiene. Esta acción no se puede deshacer.
            </Typography.Text>
          </Space>
        ) : (
          <Typography.Text>
            ¿Eliminar <Typography.Text strong>"{deleteTarget?.name}"</Typography.Text>? Esta acción no se puede deshacer.
          </Typography.Text>
        )}
      </Modal>

      {/* ── New Folder Modal ── */}
      <Modal
        title={<Space><FolderAddOutlined />Nueva carpeta en {contextLabel}</Space>}
        open={folderModalOpen}
        okText="Crear" cancelText="Cancelar"
        confirmLoading={modalSaving}
        okButtonProps={{ disabled: !newFolderName.trim() || (journeyBusy && !modalSaving) }}
        onOk={() => void handleCreateFolder()}
        onCancel={() => { setFolderModalOpen(false); setNewFolderName(''); }}
      >
        <Input
          placeholder="ej: Proyectos"
          value={newFolderName}
          onChange={(e) => setNewFolderName(e.target.value)}
          onPressEnter={() => void handleCreateFolder()}
          autoFocus
          disabled={modalSaving}
          style={{ marginTop: 8 }}
        />
      </Modal>

      {/* ── New File Modal ── */}
      <Modal
        title={<Space><FileAddOutlined />Nuevo archivo en {contextLabel}</Space>}
        open={fileModalOpen}
        okText="Crear" cancelText="Cancelar"
        confirmLoading={modalSaving}
        okButtonProps={{ disabled: !newFileName.trim() || (journeyBusy && !modalSaving) }}
        onOk={() => void handleCreateFile()}
        onCancel={() => { setFileModalOpen(false); setNewFileName(''); }}
      >
        <Input
          placeholder="ej: proceso-onboarding"
          value={newFileName}
          onChange={(e) => setNewFileName(e.target.value)}
          onPressEnter={() => void handleCreateFile()}
          autoFocus
          disabled={modalSaving}
          suffix=".md"
          style={{ marginTop: 8 }}
        />
      </Modal>

      {/* ── Document graph, full screen ── */}
      <Modal
        open={graphExpanded && graphOpen}
        onCancel={() => { setGraphExpanded(false); setSelectedEntity(null); setMentions([]); }}
        footer={null}
        destroyOnHidden
        width="100vw"
        wrapClassName="brain-graph-fullscreen"
        style={{ top: 0, maxWidth: '100vw', paddingBottom: 0 }}
        styles={{ content: { borderRadius: 0, height: '100vh', padding: 0 }, body: { height: 'calc(100vh - 56px)', padding: 0, display: 'flex' } }}
        title={<Space><ApartmentOutlined style={{ color: '#722ed1' }} />Grafo del documento{selectedFile ? ` · ${selectedFile.split('/').pop()?.replace('.md', '')}` : ''}</Space>}
      >
        <DocumentGraphBody
          layout="side"
          loading={graphLoading}
          data={graphData}
          selectedEntity={selectedEntity}
          mentions={mentions}
          mentionsLoading={mentionsLoading}
          onEntitySelect={(name) => void handleEntitySelect(name)}
        />
      </Modal>

      {/* ── "Updating the Brain" notice: a heads-up, it does not wait for the indexing ── */}
      <Modal
        title={<Space><span style={{ fontSize: 16 }}>🧠</span>{startedMessage(organization.name).title}</Space>}
        open={!!notice?.open}
        centered
        width={440}
        maskClosable
        footer={[
          <Button key="ok" type="primary" onClick={closeNotice}>
            Entendido
          </Button>,
        ]}
        onCancel={closeNotice}
      >
        <div style={{ textAlign: 'center', padding: '12px 0 4px' }}>
          <div style={{ fontSize: 42, marginBottom: 12 }}>🧠</div>
          {notice && (
            <Text style={{ fontSize: 12, color: '#bfbfbf', display: 'block', marginBottom: 10 }}>
              "{notice.label}"
            </Text>
          )}
          <Text style={{ display: 'block' }}>{startedMessage(organization.name).description}</Text>
        </div>
      </Modal>
    </>
  );
};

export default BrainPage;
